import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { buildColumnKey } from '@/services/accountService/glInvoiceService';
import type { GlCategory } from '@/services/accountService/glCategoryService';
import type { GlAccount } from '@/services/accountService/companyGLCategoryService';
import { isRateGlCode, stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import {
    columnOptionLabelWithAlias,
    resolveColumnLabelParts,
} from '@/pages/components/account/gl-invoice/columnAliasDisplay';

export type DrCrColumnOption = { key: string; label: string };

/**
 * DR/CR 下拉数据源：标准 GL Category 叶子 + 本公司 Co. GL 别名。
 * nameEn/nameZh = 标准 Item；aliasEn/aliasZh = 客户名（不再覆盖标准名）。
 */
export function buildDrCrColumnMapFromGlAndCoGl(
    leaves: GlCategory[],
    coGlAccounts: GlAccount[] | null | undefined,
): Map<string, GlColumnDef> {
    const aliasByCode = new Map<string, GlAccount>();
    for (const a of coGlAccounts ?? []) {
        if (!a?.code || a.isDel === 1 || a.isActive === 0) continue;
        aliasByCode.set(a.code.trim(), a);
    }

    const m = new Map<string, GlColumnDef>();
    for (const cat of leaves) {
        if (!cat.fsCode || !cat.code || cat.isActive === 0 || isRateGlCode(cat.code)) continue;
        const code = cat.code.trim();
        const fsCode = cat.fsCode.trim();
        const alias =
            aliasByCode.get(code)
            ?? aliasByCode.get(stripDupSuffix(code))
            ?? aliasByCode.get(fsCode);

        const nameEn = (cat.nameEn || code).trim();
        const nameZh = (cat.nameZh || '').trim();
        const aliasEn = (alias?.aliasEn || '').trim();
        const aliasZh = (alias?.aliasZh || '').trim();
        // 别名与标准名相同时不挂 alias，避免无意义「Client:」行
        const sameEn = aliasEn && aliasEn.toLowerCase() === nameEn.toLowerCase();
        const sameZh = !aliasZh || aliasZh === nameZh;

        m.set(buildColumnKey({ fsCode, glCode: code }), {
            fsCode,
            fsNameEn: '',
            glCode: code,
            nameEn,
            nameZh,
            columnIndex: 0,
            currency: alias?.currency || 'HKD',
            aliasEn: sameEn && sameZh ? undefined : (aliasEn || undefined),
            aliasZh: sameEn && sameZh ? undefined : (aliasZh || undefined),
        });
    }
    return m;
}

export function buildDrCrColumnOptions(
    columnMap: Map<string, GlColumnDef>,
    resolveFallbackZh?: (def: GlColumnDef) => string | undefined,
    showAlias = false,
): DrCrColumnOption[] {
    const options = Array.from(columnMap.values()).map((c) => ({
        key: buildColumnKey(c),
        label: columnOptionLabelWithAlias(c, resolveFallbackZh?.(c), showAlias),
    }));
    options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return options;
}

/** 搜索用：始终带上客户别名文本，便于按客户名搜到行 */
export function buildDrCrAccountLabels(
    columnMap: Map<string, GlColumnDef>,
    resolveFallbackZh?: (def: GlColumnDef) => string | undefined,
): Map<string, string> {
    const m = new Map<string, string>();
    for (const [key, col] of columnMap) {
        const parts = resolveColumnLabelParts(col, resolveFallbackZh?.(col));
        m.set(key, parts.client ? `${parts.primary} ${parts.client}` : parts.primary);
    }
    return m;
}
